<template>
  <DM ref="dm" />
  <a-modal
    :open="isOpen"
    title="SQL脚本助手"
    width="80%"
    ok-text="确认选择"
    cancel-text="取消"
    @ok="handleConfirm"
    @cancel="handleCancel"
    @close="handleModalClose"
    destroyOnClose
    :maskClosable="false"
  >
    <div class="sql-help-container">
      <!-- 搜索栏 -->
      <!-- <div class="search-bar">
        <a-input v-model:value="searchKey" placeholder="请输入脚本名称/分类关键词" allow-clear @keyup.enter="fillGrid" style="width: 97%" />
        <a-button type="primary" @click="fillGrid" style="margin-left: 8px"> 搜索 </a-button>
      </div> -->

      <!-- 主体内容：左侧树+表格纵向排列 | 右侧编辑器占满 -->
      <div class="main-content">
        <!-- 左侧区域：树 + 表格 纵向布局 -->
        <div class="content-left">
          <!-- 左侧分类树 -->
          <div class="tree-container">
            <a-tree v-model:selectedKeys="selectedTreeNodeKeys" :tree-data="treeData" :expandedKeys="expandedTreeKeys" @select="handleTreeSelect" @expand="handleTreeExpand" />
          </div>
          <!-- 脚本列表 -->
          <div class="script-list">
            <a-table
              :data-source="scriptList"
              :columns="scriptTableColumns"
              rowKey="Guid"
              :customRow="handleCustomRow"
              @cell-dblclick="handleScriptRowDblClick"
              size="small"
              :pagination="false"
              :show-header="false"
              :scroll="{ y: 340 }"
              class="sql-script-table"
            />
          </div>
        </div>

        <!-- 右侧内容区：仅保留编辑器，占满剩余空间 -->
        <div class="content-right">
          <!-- 新增全屏容器：包裹工具栏和编辑器 -->
          <div ref="fullscreenContainer" class="editor-fullscreen-container">
            <Toolbar bordered :leftItems="sqlEditTools" :rightItems="editorToolbarRight" @menu-click="handleSqlToolbarMenuClick" class="sql-editor-toolbar" />
            <!-- 编辑器 -->
            <div class="codemirror-wrapper">
              <Codemirror v-model:value="sqlContent" :options="editorOptions" style="height: 100%" @ready="handleSqlCmReady" ref="sqlCmRef" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, defineProps, defineEmits, watch, nextTick, reactive, onMounted, computed, h } from 'vue'
import { message, Modal, Input } from 'ant-design-vue'
import Codemirror from 'codemirror-editor-vue3'
import type { Editor } from 'codemirror'

// 仅导入基础样式
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/idea.css'

import DM from '/src/utils/dm.vue'
import f, { sysInfo } from '@/utils/f.ts'
import ContextMenu from '@/components/Menu/ContextMenu.vue'
import Toolbar from '@/components/Menu/Toolbar.vue'
// ====================== 类型定义 ======================
interface TreeItem {
  key: string
  title: string
  children?: TreeItem[]
}
interface ScriptItem {
  Guid: string
  ClassName: string
  Title: string
  SQLScript: string
  CreateTime: string
  idx: number
}
interface ContextMenuItem {
  key?: string
  label: string
  children?: ContextMenuItem[]
}

// ====================== 核心变量 ======================
const dm = ref(null)
const currentScript = ref<any>(null)
const sqlCmRef = ref<typeof Codemirror | null>(null)
const sqlCmInstance = ref<Editor | null>(null)

// ====================== Props/Emits ======================
const props = defineProps<{
  open: boolean
}>()
const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'confirm', scriptContent: string): void
}>()
// ====================== 内部状态 ======================
const isOpen = ref(props.open)
const searchKey = ref('')
const treeData = ref<TreeItem[]>([])
const selectedTreeNodeKeys = ref<string[]>([])
const expandedTreeKeys = ref<string[]>([])
const scriptList = ref<ScriptItem[]>([])
const currentScriptContent = ref('')

const handleSqlToolbarMenuClick = (item: ToolbarItemProps, menuKey: string) => {
  console.log('SQL编辑工具栏点击：', menuKey)
}

// 在「内部状态」区域新增全屏相关变量
const fullscreenContainer = ref<HTMLDivElement | null>(null)
const isEditorFullscreen = ref(false) // 编辑器全屏状态
const editorFullscreenZIndex = 9999 // 全屏层级（避免被遮挡）

// 编辑器全屏/退出全屏切换方法
// 优化全屏切换方法：控制整个容器全屏，而非仅编辑器
const toggleEditorFullscreen = () => {
  if (!fullscreenContainer.value) return
  isEditorFullscreen.value = !isEditorFullscreen.value
  const container = fullscreenContainer.value
  const cmInstance = sqlCmInstance.value
  if (!cmInstance) return

  if (isEditorFullscreen.value) {
    // 进入全屏：容器全屏，固定定位，层级置顶
    container.dataset.originalStyle = JSON.stringify({
      position: container.style.position,
      top: container.style.top,
      left: container.style.left,
      width: container.style.width,
      height: container.style.height,
      zIndex: container.style.zIndex,
      margin: container.style.margin,
      border: container.style.border
    })
    Object.assign(container.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      zIndex: editorFullscreenZIndex.toString(),
      margin: '0',
      border: 'none',
      background: '#fff'
    })
    document.body.style.overflow = 'hidden'
  } else {
    // 退出全屏：恢复容器原有样式
    const originalStyle = JSON.parse(container.dataset.originalStyle || '{}')
    Object.assign(container.style, originalStyle)
    delete container.dataset.originalStyle
    document.body.style.overflow = ''
  }

  // 强制刷新编辑器，适配尺寸变化
  cmInstance.refresh()
  nextTick(() => cmInstance.refresh())
}
// 重构：用 computed 自动响应全屏状态，无需 watch
const editorToolbarRight = computed(() => [
  {
    label: isEditorFullscreen.value ? '退出全屏' : '全屏',
    tooltip: isEditorFullscreen.value ? '退出编辑器全屏' : '编辑器全屏显示',
    icon: isEditorFullscreen.value ? 'fullscreen-exit' : 'fullscreen',
    onClick: toggleEditorFullscreen
  }
])

// 监听全屏状态变化，更新按钮文本/图标
// watch(isEditorFullscreen, () => {
//   editorToolbarRight[0].label = isEditorFullscreen.value ? '退出全屏' : '全屏编辑';
//   editorToolbarRight[0].tooltip = isEditorFullscreen.value ? '退出编辑器全屏' : '编辑器全屏显示';
//   editorToolbarRight[0].icon = isEditorFullscreen.value ? 'fullscreen-exit' : 'fullscreen';
// });

// 可选：添加ESC快捷键退出全屏
onMounted(() => {

  // 监听ESC键退出全屏
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isEditorFullscreen.value) {
      toggleEditorFullscreen()
    }
  })
})

// 可选：模态框关闭时强制退出全屏（避免残留）
watch(
  () => isOpen.value,
  (newVal) => {
    if (!newVal && isEditorFullscreen.value) {
      toggleEditorFullscreen()
    }
  }
)

const handleSaveScript = async () => {
  if (!currentScript.value) {
    message.warning('请先选择要修改的脚本！')
    return
  }
  try {
    const params = {
      apicode: 'S_Update_SQLHelp',
      SQLScript: sqlContent.value,
      Guid: currentScript.value.Guid,
      Where: ''
    }
    const data = await dm.value.exec('/api/comm/exec/', params)
    if (data) {
      message.success('脚本保存成功！')
      loadScriptList(currentScript.value.ClassName)
    } else {
      message.error('脚本保存失败！')
    }
  } catch (error) {
    message.error('保存脚本失败：' + (error as Error).message)
  }
}

const handleAddScript = async () => {
  if (!selectedTreeNodeKeys.value.length) {
    message.warning('请先选择脚本分类！')
    return
  }

  // 调用 F 类的 input 方法（核心修改）
  const title = await f.input(
    `新增脚本 - ${selectedTreeNodeKeys.value[0]}`, // 弹窗标题
    '请输入脚本标题：', // 提示文本
    '' // 默认值（空）
  )

  // 处理用户取消输入的情况
  if (title === null) {
    return
  }

  // 校验标题为空
  if (!title.trim()) {
    f.warning('脚本标题不能为空！')
    return
  }

  // 原有新增逻辑（无需修改）
  try {
    const params = {
      apicode: 'S_Insert_SQLHelp',
      ClassName: selectedTreeNodeKeys.value[0],
      SQLScript: sqlContent.value,
      Title: title.trim(),
      Where: ''
    }
    const data = await dm.value.exec('/api/comm/exec/', params)
    if (data) {
      f.success('脚本新增成功！')
      loadScriptList(selectedTreeNodeKeys.value[0])
    } else {
      f.error('脚本新增失败！')
    }
  } catch (error) {
    f.error('新增脚本失败：' + (error as Error).message)
  }
}

// const sqlEditTools = reactive<ToolbarItemProps[]>([
//   {
//     label: '保存脚本',
//     tooltip: '保存脚本',
//     onClick: handleSaveScript
//   },
//   {
//     label: '新增脚本',
//     tooltip: '新增脚本',
//     onClick: handleAddScript
//   }
// ])

const inputValue = ref('')
const renderInput = () => {
  return h(Input, {
    style: { width: '300px' },
    placeholder: '请输入关键词',
    value: inputValue.value,
    onChange: (e: Event) => {
      const val = (e.target as HTMLInputElement).value
      inputValue.value = val
    },
    // 监听键盘按下事件，判断回车键
    onKeydown: (e: KeyboardEvent) => {
      // antd-vue 兼容标准键盘事件，直接判断 Enter 键
      if (e.key === 'Enter' || e.keyCode === 13) {
        e.preventDefault() // 阻止默认行为（避免表单提交/页面刷新）
        fillGrid() // 执行核心逻辑
      }
    },
    // 可选：添加清除按钮（antd-vue Input 自带属性）
    allowClear: true,
    // 可选：输入框聚焦时的回调
    onFocus: () => console.log('输入框聚焦')
  })
}

// 对应C#的FillGrid方法：查询SQL脚本标题并填充到<a-table>
const fillGrid = async () => {
  try {
    // 1. 构造参数（匹配C#的sqlParameter逻辑）
    // 2. 调用S_FindSQLHelp接口（替换原有S_SelectTreeSQLScript）
    const params = {
      apicode: 'S_FindSQLHelp', // 对应C#的S_FindSQLHelp存储过程
      Title: `%${inputValue.value}%`
    }

    const data = await dm.value.data('/api/comm/data/', params)

    // 3. 处理返回数据并赋值给表格数据源（scriptList）
    if (data && data.data && Array.isArray(data.data.list)) {
      scriptList.value = data.data.list // 直接赋值给表格的data-source

      // 对应C#的gv_CellClick(null, null)：默认选中第一行
      if (scriptList.value.length > 0) {
        handleScriptRowClick(scriptList.value[0])
      }
    } else {
      // 无数据时清空表格
      scriptList.value = []
      currentScript.value = null
      sqlContent.value = ''
      return
    }
  } catch (error) {
    f.error('加载SQL脚本列表失败：' + (error as Error).message) // 复用f.ts的提示方法
    scriptList.value = [] // 异常时清空表格
  }
}

const sqlEditTools = reactive<ToolbarItemProps[]>([
  {
    type: 'label',
    label: '脚本名称'
  },
  {
    type: 'custom',
    render: renderInput
  },
  {
    label: '搜索',
    tooltip: '搜索',
    onClick: fillGrid
  },
  // 优化5：antd 工具栏分隔符类型为 'separator'（不是 'divider'）
  {
    type: 'separator'
  },
  {
    label: '新增脚本',
    tooltip: '新增脚本',
    onClick: handleAddScript
  },
  // 原有工具项
  {
    label: '保存脚本',
    tooltip: '保存脚本',
    onClick: handleSaveScript
  }
])



// 测试SQL内容
const sqlContent = ref(``)

// 编辑器配置
const editorOptions = reactive({
  mode: 'custom-mssql',
  theme: 'idea',
  lineNumbers: false,
  lineWrapping: true,
  tabSize: 4,
  readOnly: false,
  autoRefresh: true,
  ignoreModeMissing: true
})

// 脚本列表配置
const scriptTableColumns = ref([{ title: '脚本标题', dataIndex: 'Title', key: 'Title', width: 200 }])

// ====================== 核心：修复流推进问题的编辑器高亮逻辑 ======================
const handleSqlCmReady = (cm: Editor) => {
  sqlCmInstance.value = cm
  console.log('编辑器初始化完成，版本：', window.CodeMirror?.version)

const keywordList = [
  'select', 'from', 'where', 'group by', 'having', 'order by', 'distinct', 'top', 'offset', 'fetch', 'into', 'as', 'join', 'inner join', 'left join',
  'right join', 'full join', 'cross join', 'outer join', 'on', 'using', 'union', 'union all', 'intersect', 'except', 'in', 'between', 'like', 'exists',
  'any', 'all', 'some', 'insert', 'update', 'delete', 'merge', 'insert into', 'update set', 'delete from', 'values', 'set', 'output', 'default',
  'create', 'alter', 'drop', 'rename', 'truncate', 'declare', 'table', 'view', 'index', 'procedure', 'function', 'trigger', 'schema', 'database',
  'column', 'constraint', 'primary key', 'foreign key', 'unique', 'check', 'identity', 'partition', 'filegroup', 'login', 'user', 'role', 'if', 'else',
  'while', 'begin', 'end', 'case', 'when', 'then', 'break', 'continue', 'goto', 'return', 'try', 'catch', 'throw', 'raiserror',
  'waitfor', 'begin transaction', 'commit', 'rollback', 'save transaction', 'grant', 'revoke', 'deny', 'execute', 'exec', 'as', 'with', 'ownership',
  'cursor', 'open', 'fetch', 'close', 'deallocate', 'variable', 'global', 'local', '@@', 'and', 'or', 'not', 'asc', 'desc',
  'null', 'is null', 'is not null', 'not in', 'not like', 'escape', 'collate', 'over', 'partition by', 'row_number', 'rank', 'dense_rank', 'ntile',
  'for', 'xml', 'json', 'option', 'recompile', 'lock', 'nolock', 'read uncommitted', 'read committed', 'varchar', 'nvarchar', 'char', 'nchar',
  'text', 'ntext', 'int', 'bigint', 'smallint', 'tinyint', 'bit', 'decimal', 'numeric', 'float', 'real', 'money', 'smallmoney',
  'datetime', 'datetime2', 'smalldatetime', 'date', 'time', 'datetimeoffset'
];

const sqlFuncList = [
  'getdate', 'getutcdate', 'sysdatetime', 'sysdatetimeoffset', 'sysutcdatetime', 'current_timestamp', 'datepart', 'datename', 'year', 'month',
  'day', 'hour', 'minute', 'second', 'millisecond', 'microsecond', 'nanosecond', 'datefromparts', 'datetime2fromparts', 'datetimefromparts',
  'datetimeoffsetfromparts', 'smalldatetimefromparts', 'timefromparts', 'dateadd', 'datediff', 'datediff_big', 'eomonth', 'isdate', 'format',
  'switcheoffset', 'todatetimeoffset', 'datediff_legacy', 'dateadd_legacy', 'charindex', 'concat', 'concat_ws', 'datalength', 'difference',
  'left', 'len', 'lower', 'ltrim', 'patindex', 'quotename', 'replace', 'reverse', 'right', 'rtrim', 'soundex', 'space', 'str', 'stuff',
  'substring', 'trim', 'unicode', 'upper', 'ascii', 'string_escape', 'string_split', 'translate', 'abs', 'acos', 'asin', 'atan', 'atan2',
  'ceiling', 'cos', 'cot', 'degrees', 'exp', 'floor', 'log', 'log10', 'mod', 'pi', 'power', 'radians', 'rand', 'round',
  'sign', 'sin', 'sqrt', 'square', 'tan', 'big_count', 'count_big', 'avg', 'sum', 'min', 'max', 'stdev', 'stdevp', 'var',
  'varp', 'cast', 'convert', 'parse', 'try_cast', 'try_convert', 'try_parse', 'isnull', 'coalesce', 'nullif', 'iif', 'app_name',
  'collationproperty', 'col_length', 'col_name', 'current_user', 'databasepropertyex', 'db_name', 'host_name', 'is_member', 'is_rolemember',
  'object_id', 'object_name', '@@error', '@@identity', '@@rowcount', '@@version', 'suser_sname', 'user_name', 'session_user', 'system_user',
  'binary_checksum', 'checksum', 'checksum_agg', 'count', 'grouping', 'grouping_id', 'isjson', 'json_value', 'json_query', 'json_modify',
  'openjson', 'hashbytes', 'encryptbykey', 'decryptbykey', 'encryptbypassphrase', 'decryptbypassphrase', 'signbycert', 'verifybycert',
  'pwdencrypt', 'pwdcompare', 'newid', 'newsequentialid', 'row_number', 'rank', 'dense_rank', 'ntile', 'percent_rank', 'cume_dist',
  'choose', 'parsename', 'replicate', 'xp_logininfo'
];

  // 注册自定义高亮模式
  if (window.CodeMirror) {
    delete window.CodeMirror.modes['custom-mssql']

    // 替换原有正则定义 ↓↓↓
    const sortByLengthDesc = (list: string[]) => list.sort((a, b) => b.length - a.length)
    const sortedKeywords = sortByLengthDesc(keywordList)
    const sortedFuncs = sortByLengthDesc(sqlFuncList)

    // 关键修复：函数名必须满足「后接括号/空白/运算符」（仅函数调用才高亮）
    // 解释：
    // ^(${funcPattern}) → 以函数名开头
    // (?=\(|\s|$|[()=<>+*/%,.;:\-[\]{}@'"]) → 后接括号/空白/行尾/运算符
    const funcPattern = sortedFuncs.join('|')
    const funcsReg = new RegExp(`^(${funcPattern})(?=\\(|\\s|$|[()=<>+*/%,.;:\\-\\[\\]{}@'"])`, 'i')

    // 关键字同理（仅独立关键字高亮）
    const keywordPattern = sortedKeywords.join('|').replace(/ /g, '\\s')
    const keywordsReg = new RegExp(`^(${keywordPattern})(?=\\s|$|[()=<>+*/%,.;:\\-\\[\\]{}@'"])`, 'i')

    const operators = /^[()=<>+*/%,.;:\-[\]{}@'"\\]/

    // 3. 修复 token 函数中的匹配逻辑（删除 stream.backUp()）
    window.CodeMirror.defineMode('custom-mssql', () => ({
      startState: () => ({ inComment: false, inString: '' }),
      token: (stream, state) => {
        // 1. 行尾直接返回，推进流
        if (stream.eol()) {
          stream.next()
          return null
        }

        // 2. 多行注释处理
        if (state.inComment) {
          const found = stream.skipTo('*/')
          if (found) {
            stream.eat('*')
            stream.eat('/')
            state.inComment = false
          } else {
            stream.skipToEnd()
          }
          return 'comment'
        }

        // 3. 字符串处理
        if (state.inString) {
          while (!stream.eol()) {
            const ch = stream.next()
            if (ch === state.inString) {
              state.inString = ''
              break
            }
            if (ch === '\\' && stream.peek() === state.inString) {
              stream.next()
            }
          }
          return 'string'
        }

        // 4. 跳过空白符
        if (stream.eatSpace()) {
          return null
        }

        // 5. 单行注释
        if (stream.match(/^--/)) {
          stream.skipToEnd()
          return 'comment'
        }

        // 6. 多行注释开始
        if (stream.match(/^\/\*/)) {
          state.inComment = true
          return 'comment'
        }

        // 7. 字符串开始
        const ch = stream.peek()
        if (ch === "'" || ch === '"') {
          state.inString = stream.next()
          return 'string'
        }

        // 8. 数字匹配
        if (stream.match(/^\d+(\.\d+)?(e[+-]?\d+)?/i)) {
          return 'number'
        }

        // ========== 核心修复：先判断是否是连续驼峰单词 ==========
        // 匹配“大写字母开头+连续字母”的驼峰单词（如 ResultFormat），直接标记为变量（黑色）
        if (stream.match(/^[A-Z][a-zA-Z]+/)) {
          return 'variable'
        }

        // 9. 函数匹配（仅函数调用：format()/format 空格/format, 等场景）
        if (stream.match(funcsReg)) {
          return 'date-function'
        }

        // 10. 关键字匹配（仅独立关键字：select 空格/select, 等场景）
        if (stream.match(keywordsReg)) {
          return 'keyword'
        }

        // 11. 运算符匹配
        if (stream.match(operators)) {
          return 'operator'
        }

        // 12. 兜底推进流
        stream.next()
        return 'variable'
      }
    }))

    // 应用模式并强制刷新
    cm.setOption('mode', 'custom-mssql')
    cm.refresh()
    // nextTick(() => cm.refresh())
  }
}

// ====================== 数据加载方法 ======================
const loadTreeData = async () => {
  try {
    const params = {
      apicode: 'S_SQLHelpTree',
      Key: `%${searchKey.value}%`,
      Where: ''
    }
    const data = await dm.value.data('/api/comm/data/', params)
    if (data && data.data && Array.isArray(data.data.list)) {
      const treeSource = data.data.list
      const rootNode: TreeItem = { key: '', title: '脚本分类', children: [] }
      treeSource.forEach((row: any) => {
        if (row.level === 1) {
          rootNode.children?.push({ key: row.ITEMCODE, title: row.ITEMNAME })
        }
      })
      treeData.value = [rootNode]
      expandedTreeKeys.value = ['']
      if (rootNode.children?.length) {
        selectedTreeNodeKeys.value = [rootNode.children[0].key]
        loadScriptList(rootNode.children[0].title)
      }
    }
  } catch (error) {
    message.error('加载分类树失败：' + (error as Error).message)
  }
}

const loadScriptList = async (className: string) => {
  try {
    const params = {
      apicode: 'S_SelectTreeSQLScript',
      ClassName: className,
      Where: ''
    }
    const data = await dm.value.data('/api/comm/data/', params)
    if (data && data.data && Array.isArray(data.data.list)) {
      scriptList.value = data.data.list
      if (scriptList.value.length) {
        handleScriptRowClick(scriptList.value[0])
      } else {
        currentScript.value = null
        currentScriptContent.value = ''
      }
    } else {
      scriptList.value = []
      currentScript.value = null
      currentScriptContent.value = ''
    }
  } catch (error) {
    message.error('加载脚本列表失败：' + (error as Error).message)
    scriptList.value = []
    currentScript.value = null
    currentScriptContent.value = ''
  }
}

// ====================== 事件处理 ======================
const resetState = () => {
  searchKey.value = ''
  treeData.value = []
  selectedTreeNodeKeys.value = []
  scriptList.value = []
  currentScript.value = null
  currentScriptContent.value = ''
}

watch(
  () => props.open,
  (newVal) => {
    isOpen.value = newVal
    if (newVal) {
      nextTick(() => loadTreeData())
    } else {
      resetState()
    }
  },
  { immediate: true }
)

const handleSearch = () => loadTreeData()

const handleTreeSelect = (selectedKeys: string[], info: any) => {
  if (selectedKeys.length && info.node) {
    const className = info.node.title
    if (className !== '脚本分类') loadScriptList(className)
  }
}

const handleTreeExpand = (expandedKeys: string[]) => {
  expandedTreeKeys.value = expandedKeys
}

const handleCustomRow = (record: any) => {
  return {
    onClick: () => handleScriptRowClick(record)
  }
}

const handleScriptRowClick = (record: ScriptItem) => {
  currentScript.value = record
  sqlContent.value = record.SQLScript || '' // 直接赋值给编辑器绑定值
  nextTick(() => {
    if (sqlCmInstance.value) {
      sqlCmInstance.value.refresh()
    }
  })
}

const handleScriptRowDblClick = (record: ScriptItem) => {
  handleScriptRowClick(record)
  handleConfirm()
}

const handleConfirm = () => {
  if (!sqlContent.value) {
    message.warning('请选择或输入脚本内容！')
    return
  }
  emit('confirm', sqlContent.value)
  isOpen.value = false
  emit('update:open', false)
}

const handleCancel = () => {
  resetState()
  isOpen.value = false
  emit('update:open', false)
}

const handleModalClose = () => handleCancel()

// 挂载后验证

</script>

<style scoped>
.sql-help-container {
  padding: 0px 0;
  height: 65vh;
  display: flex;
  flex-direction: column;
}
.search-bar {
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}
.main-content {
  display: flex;
  flex: 1;
  gap: 4px;
}
/* 左侧区域：树 + 表格 纵向排列 */
.content-left {
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tree-container {
  flex: 0 0 180px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 8px;
  overflow-y: auto;
}
.script-list {
  flex: 1;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
}
/* 右侧编辑器区域：占满剩余空间 */
.content-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 0px;
}
.script-editor {
  flex: 1;
}

/* 表格样式修复 */
:deep(.sql-script-table) {
  height: 100%;
  margin: 0;
}
:deep(.sql-script-table .ant-table-body) {
  height: 100% !important;
}
:deep(.sql-script-table .ant-table-thead) {
  display: none;
}
:deep(.ant-tree) {
  font-size: 14px;
}
:deep(.ant-table) {
  font-size: 13px;
}

/* 编辑器样式（关键修复） */
:deep(.CodeMirror) {
  font-family: 
    Consolas,        /* Windows SSMS 标配等宽字体 */
    Menlo,           /* macOS 原生等宽字体（替代 Consolas） */
    Monaco,          /* macOS 旧版/部分Linux 等宽字体 */
    'Courier New',   /* 全平台通用等宽兜底 */
    monospace !important;
  font-size: 13px !important;
  background: #ffffff !important;
  font-weight: 400 !important;
  letter-spacing: 0px !important;
  line-height: 1.2 !important;
  -webkit-font-smoothing: antialiased !important;
  -moz-osx-font-smoothing: grayscale !important;
  text-size-adjust: 100% !important;
  -webkit-text-size-adjust: 100% !important;
}

:deep(.cm-keyword) {
  color: #0000ff !important;
  font-weight: 400 !important;
}
:deep(.cm-date-function) {
  color: #ff00ff !important;
  font-weight: 400 !important;
}
:deep(.cm-string) {
  color: #ff0000 !important;
  font-weight: 400 !important;
}
:deep(.cm-comment) {
  color: #008000 !important;
  font-weight: 400 !important;
}
:deep(.cm-number) {
  color: #333333 !important;
  font-weight: 400 !important;
}
:deep(.cm-variable) {
  color: #333333 !important;
  font-weight: 400 !important;
}


/* 保留核心样式，删除冗余 */
.ant-table-tbody > tr > td {
  height: 39px !important;
  line-height: 39px !important;
  min-height: 39px !important;
  padding: 0 16px !important;
  font-size: 14px !important;
  color: #000;
  font-family:
    -apple-system,
    SF UI Text,
    Arial,
    PingFang SC,
    Hiragino Sans GB,
    Microsoft YaHei,
    WenQuanYi Micro Hei,
    sans-serif;
  vertical-align: middle !important;
  border-bottom: 1px solid #e8e8e8;
}

:deep(.ant-table-thead > tr > th) {
  text-align: center;
  padding: 0 8px !important;
  background: #fafafa !important;
  border-bottom: 1px solid #d1e7ff !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  height: 39px !important;
  line-height: 39px !important;
  min-height: 39px !important;
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.ant-table-tbody > tr > td) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.ant-table-tbody > tr:hover > td),
:deep(.ant-table-fixed-left .ant-table-tbody > tr:hover > td),
:deep(.ant-table-fixed-right .ant-table-tbody > tr:hover > td) {
  background-color: #e5f3ff !important;
}

/* 全屏容器样式：Flex 布局，工具栏固定高度，编辑器占满剩余空间 */
.editor-fullscreen-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;
}

/* 工具栏固定样式：不随编辑器滚动，全屏时始终显示在顶部 */
.sql-editor-toolbar {
  flex: 0 0 auto; /* 固定高度，不伸缩 */
  z-index: 10; /* 确保在编辑器上方 */
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
}

/* 修复 Codemirror 高度，适应 Flex 布局 */
:deep(.editor-fullscreen-container .CodeMirror) {
  flex: 1;
  height: 100% !important;
}

/* 全屏状态下的容器样式增强 */
.editor-fullscreen-container.fullscreen-active {
  z-index: 9999;
  position: fixed !important;
}

/* 防止工具栏被遮挡 */
:deep(.toolbar-right) {
  z-index: 10000;
  position: relative;
}

/* 关键修改4：编辑器外层包裹容器，强制高度+溢出滚动 */
.codemirror-wrapper {
  flex: 1; /* 占满工具栏剩余空间 */
  height: 100%;
  overflow: auto; /* 内容过多时显示滚动条 */
  position: relative; /* 确保CodeMirror绝对定位时的父容器 */
}

:deep(.CodeMirror-line) {
  padding: 0 !important;          /* 无额外内边距 */
  line-height: inherit !important; /* 继承编辑器行高 */
  vertical-align: baseline !important; /* 基线对齐，避免 macOS 文字偏上 */
}
</style>
